<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import Nav from '$lib/components/Nav.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import IndustryTag from '$lib/components/IndustryTag.svelte';
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  import VideoPlayer from '$lib/components/VideoPlayer.svelte';
  
  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;

  let titleContent: HTMLElement;
  let prototypesCount = 0;
  let yearsCount = 0;
  let iterationsCount = 0;

  onMount(() => {
    // Setup number animations
    const metricsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateValue(0, 8, 2000, (val) => prototypesCount = val);
          animateValue(0, 4, 1000, (val) => yearsCount = val);
          animateValue(0, 2, 1500, (val) => iterationsCount = val);
          metricsObserver.disconnect();
        }
      });
    });

    const metricsSection = document.querySelector('.metrics-section');
    if (metricsSection) {
      metricsObserver.observe(metricsSection);
    }

    // Setup scroll animations
    setupProjectAnimations();
  });
</script>

<style>
  :global(.prose) {
    max-width: none;
  }
</style>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <Nav />
  <ProjectHeroSplit
    title="Project Soli"
    client="Google ATAP"
    industry="Emerging Tech"
    product="Interactive Prototypes as a Proof of Concept"
    heroMedia={{
      type: 'image',
      src: "https://www.nand.io/_next/image?url=/_next/static/media/soli-landscape.a0f7327f.jpg&w=3840&q=80"
    }}
    metrics={[
      { value: 8, label: "Prototypes" },
      { value: 4, label: "Years of Development" },
      { value: 2, label: "Products" }
    ]}
    roles="Product design, AI & machine learning, and creative lead for interaction prototypes."
  />

  <!-- Content -->
  <div class="project-section">
    <!-- Large Text Block -->
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text">
        Designing feasible prototypes for Google's new interaction sensor using radar technology.
      </p>
    </div>

    <!-- Challenge Section -->
    <div class="prose prose-lg max-w-none mb-16 animate-on-view" data-delay="2">
      <p class="text-gray-700">
        In 2015, Google Advanced Technology and Projects, led by Ivan Poupyrev, developed a new sensing technology: Project Soli. The challenge was to create interactions for a new technology triggered by human motions at various scales, from heartbeat to body movements, all while fitting onto a chip that could be produced at scale and built into small devices and everyday objects.
      </p>
    </div>

    <!-- Chip Prototypes Image -->
    <section class="animate-on-view mb-16" data-delay="3">
      <img 
        src="https://www.nand.io/_next/image?url=/_next/static/media/chip-prototypes-collage_2x.143e2526.jpg&w=3840&q=80" 
        alt="Soli Chip Prototypes" 
        class="w-full"
      />
      <p class="text-sm text-gray-600 mt-4">Radar chip evolution at Google</p>
    </section>

    <!-- Quote Section -->
    <section class="py-8 animate-on-view mb-8" data-delay="4">
      <div class="container py-16 max-w-4xl">
        <div class="grid grid-cols-12">
          <div class="col-span-12">
            <div class="relative">
              <span 
                class="absolute -top-8 text-[120px] leading-none text-[#F96604] font-serif"
                aria-hidden="true"
                style="opacity: 1"
              >
                ”
              </span>
              <blockquote class="py-12 text-xl md:text-4xl font-light" style="color: hsla(226, 65%, 35%, 1); line-height: 1.4;">
                The hand is the ultimate input device. It's extremely precise, it's extremely fast, and it's very natural for us to use it.
              </blockquote>
              <footer class="text-sm mt-4 text-gray-600">Ivan Poupyrev, Director of Engineering ATAP</footer>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Value Delivered Section -->
    <div class="prose prose-lg max-w-none mb-16 animate-on-view" data-delay="5">
      <p class="text-gray-700">
        We developed practical use cases and operational prototypes utilizing the Soli chip. Through machine learning and AI, we trained Soli to recognize a series of gestures we designed. The prototypes served as proof of concept for various product ideas and innovations. We also handled the full software development and created comprehensive documentation for the Soli Dev Kit, including demos and gesture kit explanations.
      </p>
    </div>

    <!-- ML Illustrations -->
    <section class="animate-on-view mb-32" data-delay="6">
      <img 
        src="https://www.nand.io/_next/image?url=/_next/static/media/ml-illustrations.cfa16224.png&w=3840&q=80" 
        alt="Machine Learning Illustrations" 
        class="w-full"
      />
    </section>

    <!-- Prototypes Grid -->
    <section class="animate-on-view mb-32" data-delay="7">
      <h2 class="text-2xl font-light mb-8">Prototypes</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <div class="aspect-video mb-4">
            <VideoPlayer
              src="https://vz-6b226b53-3c8.b-cdn.net/375486f6-d90f-4c34-9e68-171ba5817c53/playlist.m3u8"
              autoplay={true}
              muted={true}
              loop={true}
              controls={false}
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
          <h3 class="text-lg font-medium mb-2">Gesture Recognition</h3>
          <p class="text-sm text-gray-600">Real-time hand gesture detection and tracking</p>
        </div>
        <div>
          <div class="aspect-video mb-4">
            <VideoPlayer
              src="https://vz-6b226b53-3c8.b-cdn.net/7c1a59a4-957a-4929-bb5b-ca80840ff046/playlist.m3u8"
              autoplay={true}
              muted={true}
              loop={true}
              controls={false}
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
          <h3 class="text-lg font-medium mb-2">Motion Control</h3>
          <p class="text-sm text-gray-600">Precise control through micro-gestures</p>
        </div>
        <div>
          <div class="aspect-video mb-4">
            <VideoPlayer
              src="https://vz-6b226b53-3c8.b-cdn.net/f2e68c35-8587-4f63-ba55-b163cd105869/playlist.m3u8"
              autoplay={true}
              muted={true}
              loop={true}
              controls={false}
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
          <h3 class="text-lg font-medium mb-2">Interface Interaction</h3>
          <p class="text-sm text-gray-600">Natural interaction with digital interfaces</p>
        </div>
      </div>
    </section>
  </div>

  <!-- Credits Section -->
  <div class="animate-on-view" style="background-color: #f9fafb;">
    <div class="max-w-3xl mx-auto px-8 py-12">
      <div class="flex flex-col md:flex-row justify-between items-start gap-8">
        <div>
          <p class="text-sm text-gray-500 mb-4">Project lead by</p>
          <div class="space-y-1">
            <p class="text-sm">Ivan Poupyrev</p>
          </div>
        </div>
        <div>
          <p class="text-sm text-gray-500 mb-4">Core team</p>
          <div class="space-y-1">
            <p class="text-sm">Nicholas Gillian</p>
            <p class="text-sm">Hakim Raja</p>
            <p class="text-sm">Erik Olson</p>
            <p class="text-sm">Jaime Lien</p>
            <p class="text-sm">Patrick Amihood</p>
            <p class="text-sm">Emre Karagozler</p>
          </div>
        </div>
        <div>
          <p class="text-sm text-gray-500 mb-4">In collaboration with</p>
          <div class="space-y-1">
            <p class="text-sm">Tim Gferer</p>
            <p class="text-sm">Arturo Castro</p>
            <p class="text-sm">Tomek Ness</p>
            <p class="text-sm">Jochen Maria Weber</p>
            <p class="text-sm">Sebastian An</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Project Navigation -->
  <ProjectNav 
    prevProject={{ title: "FactSnap", url: "/projects/fact-snap" }}
    nextProject={{ title: "Pandemieradar", url: "/projects/pandemieradar" }}
  />

  <Footer />
</div> 